import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { BlockStack, Button, DatePicker, Text } from '@shopify/polaris';
import { CalendarIcon, XSmallIcon } from '@shopify/polaris-icons';
import { Ic, StatusPill } from '../ui';

export const ItCtx = createContext(null);
export const useIt = () => useContext(ItCtx);

export const STATUS = {
  stock: ['In stock', 'ful'], deployed: ['Deployed', 'par'], loan: ['On loan', 'hold'], repair: ['In repair', 'can'],
  retired: ['Retired', 'neu'], lost: ['Lost', 'can'], stolen: ['Stolen', 'can'], disposed: ['Disposed', 'neu'],
};
export const GONE = ['retired', 'lost', 'stolen', 'disposed'];
export const KIND_LABEL = { store: 'Store', teams: 'Teams', warehouse: 'Warehouse', office: 'Office', home: 'Home office', venue: 'Offsite venue', inventory: 'Inventory', other: 'Other' };

export const StatusBadge = ({ status }) => <StatusPill tone={(STATUS[status] || ['', 'neu'])[1]}>{(STATUS[status] || [status])[0]}</StatusPill>;

export function LocChip({ name, kind }) {
  if (!name) return <span className="uh-faint">none</span>;
  return <span className="uh-ia-loc"><span className="loc">{name}</span>{kind && <span className={`uh-ia-lt ${kind}`}>{KIND_LABEL[kind] || kind}</span>}</span>;
}

// Where a device is, plus the store it counts toward when that is a different place (an offsite event).
// Who holds a device: a chip like the location's when it is someone on the People tab; a typed use or an old sheet name stays plain text.
export function WhoCell({ a }) {
  if (a.person) return <span className="uh-ia-loc"><span className="loc">{a.person}</span><span className="uh-ia-lt person">Person</span></span>;
  return a.holder ? <span className="uh-ia-old" title="A use, or a name from the old spreadsheet. Not someone on the People tab.">{a.holder}</span> : null;
}

export function WhereCell({ a }) {
  return (
    <>
      {!a.location && a.where_text ? <span className="uh-ia-old" title="From the old spreadsheet. Not a known location yet.">{a.where_text}</span> : <LocChip name={a.location} kind={a.location_kind} />}
      {a.store && a.store !== a.location && <div className="uh-ia-sm">counts toward {a.store}</div>}
    </>
  );
}

export const money = (n) => (n === null || n === undefined || n === '' ? '' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
export const dateOnly = (d) => (d ? String(d).slice(0, 10) : '');
export function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
export const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
export const tagText = (a) => a.tag || 'No tag yet';

export function dueBadge(a, todayIso) {
  if (a.status !== 'loan') return null;
  if (!a.due_on) return <StatusPill tone="can">No return date</StatusPill>;
  const due = dateOnly(a.due_on);
  if (due < todayIso) return <StatusPill tone="can">Overdue since {due}</StatusPill>;
  const days = (new Date(due) - new Date(todayIso)) / 86400000;
  return days <= 7 ? <StatusPill tone="hold">Due {due}</StatusPill> : <span>{due}</span>;
}

export function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export function Modal({ title, subtitle, onClose, children, footer, wide, focusFirst }) {
  const ref = useRef(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // showModal() moves focus to the first focusable thing, the Close button; a form wants its first field.
    if (d && focusFirst) { const el = d.querySelector('.uh-ia-dlg-b input:not([type=hidden]):not([type=checkbox]):not([type=file]), .uh-ia-dlg-b select, .uh-ia-dlg-b textarea'); if (el) el.focus(); }
    return () => { if (d && d.open) d.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    // stopPropagation: React re-dispatches cancel and click up the component tree, so a
    // dialog stacked inside another (Edit receipt, the file preview) must not close its parent.
    <dialog ref={ref} className={`uh-dlg uh-ia-dlg${wide ? ' wide' : ''}`} aria-label={title} onCancel={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
      onClick={(e) => { e.stopPropagation(); if (e.target === ref.current) onClose(); }}>
      <div className="uh-dlg-h">
        <h2>{title}</h2>
        {subtitle && <span className="uh-muted uh-ia-subt">{subtitle}</span>}
        <span className="uh-sp" />
        <button type="button" className="uh-ibtn" aria-label="Close" onClick={onClose}><Ic source={XSmallIcon} sm /></button>
      </div>
      <div className="uh-ia-dlg-b">{children}</div>
      {footer && <div className="uh-ia-dlg-f">{footer}</div>}
    </dialog>
  );
}

export function Field({ label, children, hint, required }) {
  return (
    <label className="uh-ia-fld">
      <span className="lab">{label}{required && <span className="req"> *</span>}</span>
      {children}
      {hint && <span className="uh-ia-hint">{hint}</span>}
    </label>
  );
}

export function Alert({ children }) {
  if (!children) return null;
  return <div className="uh-alert" role="alert"><div>{children}</div></div>;
}

// A Polaris date field for forms that live inside a native <dialog>: a button
// showing the date opens a Polaris DatePicker in a panel anchored here, not in
// Polaris's own Popover, which renders outside the dialog and would sit behind it.
// value is "YYYY-MM-DD" or ""; onChange gets the same.
export function DateField({ label, value, onChange, helpText }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = value ? new Date(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10))) : null;
  const [{ month, year }, setMonthYear] = useState(() => { const d = selected || new Date(); return { month: d.getMonth(), year: d.getFullYear() }; });
  useEffect(() => { if (selected) setMonthYear({ month: selected.getMonth(), year: selected.getFullYear() }); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const text = selected ? selected.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Pick a date';
  return (
    <div className="uh-ia-pdate" ref={ref}>
      <BlockStack gap="100">
        <Text as="span" variant="bodyMd">{label}</Text>
        <Button icon={CalendarIcon} disclosure fullWidth textAlign="left" onClick={() => setOpen((o) => !o)}>{text}</Button>
        {helpText && <Text as="span" variant="bodyMd" tone="subdued">{helpText}</Text>}
      </BlockStack>
      {open && (
        <div className="uh-ia-pdate-pop">
          <DatePicker month={month} year={year} onMonthChange={(m, y) => setMonthYear({ month: m, year: y })}
            selected={selected ? { start: selected, end: selected } : undefined}
            onChange={({ start }) => { onChange(`${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`); setOpen(false); }} />
          <div className="acts">
            <Button size="slim" onClick={() => { onChange(''); setOpen(false); }}>Clear</Button>
            <Button size="slim" variant="primary" onClick={() => setOpen(false)}>Done</Button>
          </div>
        </div>
      )}
    </div>
  );
}
